import React, { useState } from 'react'
import * as Yup from 'yup'
import { Formik } from 'formik'
import { HiDotsHorizontal } from 'react-icons/hi'
import { BoxContainer, Button, Table } from '../../../../../../components'
import StatusBadge from '../../../../../../components/StatusBadge'
import HeaderItem from '../../../../../../components/Table/HeaderItem'
import RowItem from '../../../../../../components/Table/RowItem'
import {
  TooltipButton,
  TooltipMenu
} from '../../../../../../components/Tooltip'
import {
  CouponStatus,
  CouponStatusColors,
  getCouponStatusText
} from '../../../../../../constants/coupon'
import { dateTypes } from '../../../../../../constants/date'
import { PaginationOrder } from '../../../../../../constants/pagination'
import dayjs from '../../../../../../helpers/dayjs'
import { formatToPercentage } from '../../../../../../helpers/formatToPercentage'
import { useInfinitePagination } from '../../../../../../hooks/useInfinitePagination'
import { useModal } from '../../../../../../hooks/useModal'
import ecbrApi from '../../../../../../services/ecbrApi'
import CouponModal from './CouponModal'
import { TopButtonsContainer } from './styles'
import { Form } from '../../../../../../components/Form/styles'
import Input from '../../../../../../components/Form/Input'
import ErrorMessage from '../../../../../../components/Form/ErrorMessage'
import { extractErrorMessage } from '../../../../../../helpers/extractErrorMessage'

const pagination = { page: 1, perPage: 10 }

const Coupons = () => {
  const [renderModal, openModal] = useModal(CouponModal)
  const [error, setError] = useState('')
  const searchParameters = {
    order: PaginationOrder.DESC,
    page: pagination.page,
    take: pagination.perPage
  }
  const [couponEditing, setCouponEditing] = useState(null)
  const [couponEditingError, setCouponEditingError] = useState('')

  const {
    data: coupons,
    fetchNextPage,
    hasNextPage,
    isLastPage,
    isFetching,
    isLoading,
    refetch
  } = useInfinitePagination(
    'coupons',
    async (params) => {
      const { pageParam = searchParameters.page } = params
      return ecbrApi.coupons.getAll({
        ...searchParameters,
        page: pageParam || searchParameters.page
      })
    },
    {
      onSuccess: () => {
        setError('')
      },
      onError: () => {
        setError('Não foi possível carregar os dados')
      }
    }
  )

  const handleDisableCoupon = async ({ id, percentage, ...coupon }) => {
    try {
      const couponDisabled = {
        ...coupon,
        status: CouponStatus.INACTIVE,
        percentage: Number(percentage)
      }
      await ecbrApi.coupons.update(id, couponDisabled)
      refetch()
    } catch (err) {
      setError(
        `Não foi possível desativar o cupom ${coupon.name}. Erro: ${err.message}`
      )
    }
  }

  const handleEnableCoupon = async ({ id, percentage, ...coupon }) => {
    try {
      const couponEnabled = {
        ...coupon,
        status: CouponStatus.ACTIVE,
        percentage: Number(percentage)
      }
      await ecbrApi.coupons.update(id, couponEnabled)
      refetch()
    } catch (err) {
      setError(
        `Não foi possível ativar o cupom ${coupon.name}. Erro: ${err.message}`
      )
    }
  }

  const saveCoupon = async (coupon) => {
    try {
      setCouponEditingError('')
      if (!coupon.id) {
        throw new Error('Cupom não encontrado')
      }

      const payload = {
        quantity: coupon.quantity,
        expiration: dayjs(coupon.expirationDate).format(dateTypes.endDate)
      }
      await ecbrApi.coupons.update(coupon.id, payload)

      setCouponEditing(null)
      refetch()
    } catch (err) {
      const errorMessage = extractErrorMessage(err)
      setCouponEditingError(`O cupom não foi alterado: ${errorMessage}`)
    }
  }

  return (
    <>
      {renderModal}
      <TopButtonsContainer>
        <Button
          text="Adicionar cupom"
          variant="outlined"
          width="180px"
          onClick={() => openModal({ onExited: () => refetch() })}
        />
      </TopButtonsContainer>

      <BoxContainer>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <Table templateColumns="14% 25% 12% 12% 12% 12% 8%">
          <header>
            <ul>
              <HeaderItem>Identificação</HeaderItem>
              <HeaderItem>Nome do cupom</HeaderItem>
              <HeaderItem>Porcentagem</HeaderItem>
              <HeaderItem>Quantidade</HeaderItem>
              <HeaderItem>Expiração</HeaderItem>
              <HeaderItem>Status</HeaderItem>
              <HeaderItem>Ações</HeaderItem>
            </ul>
          </header>
          <main>
            {coupons?.map?.((coupon) => (
              <React.Fragment key={coupon.id}>
                {couponEditing?.id !== coupon.id ? (
                  <ul>
                    <RowItem>{coupon.code}</RowItem>
                    <RowItem>{coupon.name}</RowItem>
                    <RowItem>{formatToPercentage(coupon.percentage)}</RowItem>
                    <RowItem>{`${coupon.quantity - coupon.availableQuantity}/${
                      coupon.quantity
                    }`}</RowItem>
                    <RowItem>
                      {dayjs(coupon.expiration).format(dateTypes.dateBR)}
                    </RowItem>
                    <RowItem>
                      <StatusBadge
                        text={getCouponStatusText(coupon.status)}
                        color={CouponStatusColors[coupon.status.toUpperCase()]}
                      />
                    </RowItem>
                    <RowItem>
                      <TooltipMenu
                        icon={<HiDotsHorizontal />}
                        name="userOptions"
                        closeOnClick
                      >
                        {coupon.status === CouponStatus.ACTIVE && (
                          <TooltipButton
                            text="Desativar cupom"
                            onClick={() => handleDisableCoupon(coupon)}
                          />
                        )}
                        {coupon.status === CouponStatus.INACTIVE && (
                          <TooltipButton
                            text="Ativar cupom"
                            onClick={() => handleEnableCoupon(coupon)}
                          />
                        )}
                        <TooltipButton
                          text="Editar cupom"
                          onClick={() => setCouponEditing(coupon)}
                        />
                      </TooltipMenu>
                    </RowItem>
                  </ul>
                ) : (
                  <Formik
                    initialValues={{
                      quantity: coupon.quantity,
                      expirationDate: dayjs(coupon.expiration).format(
                        dateTypes.date
                      )
                    }}
                    validationSchema={Yup.object().shape({
                      quantity: Yup.number()
                        .required('Campo obrigatório')
                        .integer(),
                      expirationDate: Yup.date()
                        .required('Campo obrigatório')
                        .test(
                          'date-after-today',
                          'Data deve ser maior ou igual do que a atual',
                          (value) => {
                            const couponExpiration = dayjs(
                              coupon.expiration
                            ).format(dateTypes.date)
                            const today = dayjs().format(dateTypes.date)
                            return (
                              dayjs(value).isSame(couponExpiration) ||
                              dayjs(value).isSame(today) ||
                              dayjs(value).isAfter(today)
                            )
                          }
                        )
                    })}
                    onSubmit={(values) => {
                      saveCoupon({
                        ...coupon,
                        ...values
                      })
                    }}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur
                    }) => (
                      <Form display="block" maxWidth="100%" margin="0 0 8px 0">
                        <ul>
                          <RowItem>{coupon.code}</RowItem>
                          <RowItem>{coupon.name}</RowItem>
                          <RowItem>
                            {formatToPercentage(coupon.percentage)}
                          </RowItem>
                          <RowItem>
                            <Input
                              name="quantity"
                              type="number"
                              min={0}
                              value={values.quantity}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.quantity && touched.quantity}
                            />
                          </RowItem>
                          <RowItem>
                            <Input
                              name="expirationDate"
                              type="date"
                              value={values.expirationDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                errors.expirationDate && touched.expirationDate
                              }
                            />
                          </RowItem>
                          <RowItem>
                            <StatusBadge
                              text={getCouponStatusText(coupon.status)}
                              color={
                                CouponStatusColors[coupon.status.toUpperCase()]
                              }
                            />
                          </RowItem>
                          <RowItem>
                            <Button
                              text="Salvar"
                              variant="outlinedLink"
                              padding="0"
                              type="submit"
                            />
                          </RowItem>
                        </ul>
                        {couponEditingError && (
                          <ErrorMessage>{couponEditingError}</ErrorMessage>
                        )}
                      </Form>
                    )}
                  </Formik>
                )}
              </React.Fragment>
            ))}
          </main>
          <footer>
            {hasNextPage && !isLastPage && (
              <Button
                type="button"
                variant="outlined"
                text={isLoading || isFetching ? 'Carregando...' : 'Ver mais'}
                width="120px"
                onClick={() => fetchNextPage()}
              />
            )}
          </footer>
        </Table>
      </BoxContainer>
    </>
  )
}

export default Coupons
